import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import usePageTitle from '../hooks/usePageTitle';
import './FlowDetail.css';

const FlowDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [flow, setFlow] = useState(null);
  usePageTitle(flow ? flow.name : 'Flow Details');
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('runs');
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [selectedMode, setSelectedMode] = useState('incremental');
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewType, setPreviewType] = useState('output');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editFlow, setEditFlow] = useState(null);
  // New state for rich field selection
  const [availableFields, setAvailableFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [expandedRelations, setExpandedRelations] = useState({});
  const [relatedFields, setRelatedFields] = useState({});
  const [dependencies, setDependencies] = useState(null);
  const [loadingDependencies, setLoadingDependencies] = useState(false);
  const [creatingDependencies, setCreatingDependencies] = useState(false);
  const [fieldSearchQuery, setFieldSearchQuery] = useState('');

  useEffect(() => {
    fetchFlowDetails();
    fetchStats();
    fetchRuns();
    fetchDependencies();
  }, [id]);

  const fetchFlowDetails = async () => {
    try {
      const response = await axiosInstance.get(`/etl/flows/${id}/`);
      setFlow(response.data);
      setEditFlow({
        name: response.data.name,
        description: response.data.description,
        odoo_model: response.data.odoo_model,
        bq_table: response.data.bq_table,
        extraction_config: response.data.extraction_config || { fields: [], domain: [] }
      });
      setLoading(false);
    } catch (err) {
      setError('Failed to load flow details');
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get(`/etl/flows/${id}/statistics/`);
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  };

  const fetchRuns = async () => {
    try {
      const response = await axiosInstance.get(`/etl/flows/${id}/runs/`);
      setRuns(response.data || []);
    } catch (err) {
      console.error('Failed to load runs:', err);
      setRuns([]);
    }
  };

  const fetchDependencies = async () => {
    setLoadingDependencies(true);
    try {
      const response = await axiosInstance.get(`/etl/flows/${id}/dependencies/`);
      setDependencies(response.data);
    } catch (err) {
      console.error('Failed to load dependencies:', err);
      setDependencies(null);
    } finally {
      setLoadingDependencies(false);
    }
  };

  const createDependencyFlows = async () => {
    if (!confirm('This will analyze your flow\'s multi-level fields and create separate flows for related models. Continue?')) {
      return;
    }

    setCreatingDependencies(true);
    try {
      // Update flow to enable auto-create
      const currentConfig = flow.extraction_config || {};
      const updatedConfig = {
        ...currentConfig,
        auto_create_related_flows: true,
        skip_multi_level_resolution: true
      };

      await axiosInstance.put(`/etl/flows/${id}/`, {
        ...flow,
        extraction_config: updatedConfig
      });

      // Refresh to see new dependencies
      await fetchFlowDetails();
      await fetchDependencies();

      alert('✅ Dependency flows created successfully! Check the Dependencies section below.');
    } catch (err) {
      alert('Failed to create dependency flows: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreatingDependencies(false);
    }
  };

  const fetchPreview = async (type = 'output', limit = 10) => {
    setPreviewLoading(true);
    try {
      const response = await axiosInstance.get(`/etl/flows/${id}/preview/`, {
        params: { type, limit }
      });
      setPreviewData(response.data);
      setPreviewType(type);
      setPreviewLoading(false);
    } catch (err) {
      console.error('Failed to load preview:', err);
      setPreviewData({ error: err.response?.data?.error || 'Failed to load preview' });
      setPreviewLoading(false);
    }
  };

  const triggerFlow = async (mode = null) => {
    const runMode = mode || selectedMode;

    try {
      await axiosInstance.post(`/etl/flows/${id}/trigger/`, {
        mode: runMode
      });
      alert(`Flow triggered successfully in ${runMode} mode!`);
      setShowModeSelector(false);
      setTimeout(() => {
        fetchStats();
        fetchRuns();
      }, 1000);
    } catch (err) {
      alert('Failed to trigger flow: ' + (err.response?.data?.error || err.message));
    }
  };

  const deleteFlow = async () => {
    setDeleting(true);
    try {
      await axiosInstance.delete(`/etl/flows/${id}/`);
      navigate('/flows');
    } catch (err) {
      alert('Failed to delete flow: ' + (err.response?.data?.error || err.message));
      setDeleting(false);
    }
  };

  const saveFlow = async () => {
    try {
      const response = await axiosInstance.put(`/etl/flows/${id}/`, editFlow);
      setFlow(response.data);
      setIsEditing(false);
      alert('Pipeline updated successfully!');
    } catch (err) {
      alert('Failed to update pipeline: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleFieldChange = (fieldName) => {
    const currentFields = editFlow.extraction_config.fields || [];
    let newFields;
    const isSelected = currentFields.some(f => (typeof f === 'string' ? f : f.name) === fieldName);

    if (isSelected) {
      newFields = currentFields.filter(f => (typeof f === 'string' ? f : f.name) !== fieldName);
    } else {
      // Find the field in availableFields or relatedFields to enrich it
      const fData = availableFields.find(f => f.name === fieldName);
      if (fData) {
        newFields = [...currentFields, { name: fData.name, label: fData.label, type: fData.type }];
      } else {
        // Check related fields
        let found = false;
        for (const key in relatedFields) {
          const relF = relatedFields[key].find(rf => rf.dotted_name === fieldName);
          if (relF) {
            newFields = [...currentFields, { name: relF.dotted_name, label: relF.label, type: relF.type, is_related: true }];
            found = true;
            break;
          }
        }
        if (!found) newFields = [...currentFields, fieldName];
      }
    }
    setEditFlow({
      ...editFlow,
      extraction_config: {
        ...editFlow.extraction_config,
        fields: newFields
      }
    });
  };

  const addCustomField = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      handleFieldChange(e.target.value.trim());
      e.target.value = '';
    }
  };

  const fetchAvailableFields = async (modelName) => {
    setLoadingFields(true);
    try {
      const response = await axiosInstance.get('/etl/flows/odoo_fields/', {
        params: { model: modelName }
      });
      setAvailableFields(response.data.fields || []);
    } catch (err) {
      console.error('Failed to load fields:', err);
    } finally {
      setLoadingFields(false);
    }
  };

  const enterEditMode = () => {
    setIsEditing(true);
    if (flow?.odoo_model) {
      fetchAvailableFields(flow.odoo_model);
    }
  };

  const expandRelation = async (field) => {
    const key = `${flow.odoo_model}.${field.name}`;

    if (expandedRelations[key]) {
      const newExpanded = { ...expandedRelations };
      delete newExpanded[key];
      setExpandedRelations(newExpanded);
      return;
    }

    try {
      const response = await axiosInstance.get('/etl/flows/odoo_related_fields/', {
        params: { model: flow.odoo_model, field: field.name }
      });

      setRelatedFields({
        ...relatedFields,
        [key]: response.data.related_fields
      });
      setExpandedRelations({
        ...expandedRelations,
        [key]: true
      });
    } catch (err) {
      console.error('Failed to load related fields:', err);
    }
  };

  const getFieldIcon = (fieldType) => {
    const icons = {
      'char': '📝', 'text': '📄', 'integer': '🔢', 'float': '💰',
      'boolean': '✓', 'date': '📅', 'datetime': '🕐',
      'many2one': '🔗', 'one2many': '📋', 'many2many': '🔗', 'selection': '☑️',
    };
    return icons[fieldType] || '📌';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'warning', icon: '⏳' },
      extracting: { class: 'info', icon: '📥' },
      uploading: { class: 'info', icon: '☁️' },
      processing: { class: 'info', icon: '⚙️' },
      loading: { class: 'info', icon: '📤' },
      completed: { class: 'success', icon: '✅' },
      failed: { class: 'error', icon: '❌' },
      cancelled: { class: 'secondary', icon: '⛔' },
    };
    return statusConfig[status] || statusConfig.pending;
  };

  if (loading) {
    return (
      <div className="flow-detail-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <span>Loading pipeline...</span>
        </div>
      </div>
    );
  }

  if (error || !flow) {
    return (
      <div className="flow-detail-container">
        <div className="error-state">{error || 'Pipeline not found'}</div>
        <button onClick={() => navigate('/flows')} className="btn-primary">
          Back to Pipelines
        </button>
      </div>
    );
  }

  const successRate = flow.total_runs > 0
    ? Math.round((flow.successful_runs / flow.total_runs) * 100)
    : 0;

  return (
    <div className="flow-detail-container">
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Pipeline</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{flow?.name}</strong>?</p>
              <p className="modal-warning">This action cannot be undone. All run history will be deleted.</p>
            </div>
            <div className="modal-actions">
              <button
                className="modal-btn-cancel"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="modal-btn-delete"
                onClick={deleteFlow}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Pipeline'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="page-header-bar">
        <button onClick={() => navigate('/flows')} className="btn-back">
          ← Back
        </button>
        <div className="header-actions">
          {isEditing ? (
            <>
              <button onClick={() => setIsEditing(false)} className="btn-cancel-edit">
                Cancel
              </button>
              <button onClick={saveFlow} className="btn-save">
                Save Changes
              </button>
            </>
          ) : (
            <>
              <button onClick={enterEditMode} className="btn-edit-outline">
                Edit
              </button>
              <button onClick={() => setShowDeleteModal(true)} className="btn-danger">
                Delete
              </button>
              {showModeSelector ? (
                <div className="mode-selector">
                  <select
                    value={selectedMode}
                    onChange={(e) => setSelectedMode(e.target.value)}
                    className="mode-select"
                  >
                    <option value="incremental">Incremental</option>
                    <option value="full">Full Refresh</option>
                    {flow.flow_type === 'bronze' && (
                      <option value="delete_sync">Delete Sync (ID check)</option>
                    )}
                  </select>
                  <button onClick={() => triggerFlow()} className="btn-primary">
                    Run
                  </button>
                  <button onClick={() => setShowModeSelector(false)} className="btn-cancel-small">
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowModeSelector(true)}
                  className="btn-primary"
                  disabled={flow.status !== 'active'}
                >
                  Run Pipeline
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Pipeline Info Section */}
      <div className="pipeline-info-section">
        <div className="info-main">
          {isEditing ? (
            <div className="edit-info-fields">
              <input
                type="text"
                value={editFlow.name}
                onChange={(e) => setEditFlow({ ...editFlow, name: e.target.value })}
                className="edit-input-title"
                placeholder="Pipeline Name"
              />
              <textarea
                value={editFlow.description}
                onChange={(e) => setEditFlow({ ...editFlow, description: e.target.value })}
                className="edit-textarea-desc"
                placeholder="Pipeline Description"
              />
            </div>
          ) : (
            <>
              <div className="pipeline-title-row">
                <h1>{flow.name}</h1>
                <span className={`status-pill status-${flow.status}`}>{flow.status}</span>
              </div>
              {flow.description && <p className="pipeline-desc">{flow.description}</p>}
            </>
          )}
        </div>

        {/* Quick Stats Row */}
        <div className="quick-stats-row">
          <div className="quick-stat">
            <span className="stat-num">{flow.total_runs}</span>
            <span className="stat-name">Runs</span>
          </div>
          <div className="stat-divider"></div>
          <div className="quick-stat success">
            <span className="stat-num">{flow.successful_runs}</span>
            <span className="stat-name">Success</span>
          </div>
          <div className="stat-divider"></div>
          <div className="quick-stat error">
            <span className="stat-num">{flow.failed_runs}</span>
            <span className="stat-name">Failed</span>
          </div>
          <div className="stat-divider"></div>
          <div className="quick-stat">
            <span className="stat-num">{successRate}%</span>
            <span className="stat-name">Success Rate</span>
          </div>
        </div>
      </div>

      <div className="config-table-section">
        <h2>Configuration</h2>
        <table className="config-table">
          <tbody>
            <tr>
              <td className="config-key">Source Model</td>
              <td className="config-val">
                {isEditing ? (
                  <input
                    type="text"
                    value={editFlow.odoo_model}
                    onChange={(e) => setEditFlow({ ...editFlow, odoo_model: e.target.value })}
                    className="edit-input"
                  />
                ) : (
                  <code>{flow.odoo_model}</code>
                )}
              </td>
              <td className="config-key">BigQuery Table</td>
              <td className="config-val">
                {isEditing ? (
                  <input
                    type="text"
                    value={editFlow.bq_table}
                    onChange={(e) => setEditFlow({ ...editFlow, bq_table: e.target.value })}
                    className="edit-input"
                  />
                ) : (
                  <code>{flow.bq_project_id}.{flow.bq_dataset}.{flow.bq_table}</code>
                )}
              </td>
            </tr>
            <tr>
              <td className="config-key">Fields</td>
              <td className="config-val" colSpan="3">
                {isEditing ? (
                  <div className="field-selector-edit">
                    {loadingFields ? (
                      <div className="loading-fields">Loading available fields...</div>
                    ) : availableFields.length > 0 ? (
                      <div className="available-fields-grid">
                        {availableFields.map(field => {
                          const isSelected = (editFlow.extraction_config?.fields || []).some(f => (typeof f === 'string' ? f : f.name) === field.name);
                          const relationKey = `${flow.odoo_model}.${field.name}`;
                          const isExpanded = expandedRelations[relationKey];
                          const hasRelatedFields = relatedFields[relationKey];

                          return (
                            <div key={field.name} className="field-item-edit">
                              <label className={`field-checkbox ${isSelected ? 'selected' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleFieldChange(field.name)}
                                />
                                <span className="field-icon-edit">{getFieldIcon(field.type)}</span>
                                <span className="field-label-edit">{field.label}</span>
                                <code className="field-name-edit">{field.name}</code>
                                <span className="field-type-edit">{field.type}</span>
                              </label>
                              {field.is_relational && (
                                <button
                                  className="expand-related-btn"
                                  onClick={() => expandRelation(field)}
                                  title={`Expand ${field.relation}`}
                                >
                                  {isExpanded ? '▼' : '▶'}
                                </button>
                              )}
                              {isExpanded && hasRelatedFields && (
                                <div className="related-fields-edit">
                                  {hasRelatedFields.map(relField => {
                                    const relIsSelected = (editFlow.extraction_config?.fields || []).includes(relField.dotted_name);
                                    return (
                                      <label key={relField.dotted_name} className={`field-checkbox related ${relIsSelected ? 'selected' : ''}`}>
                                        <input
                                          type="checkbox"
                                          checked={relIsSelected}
                                          onChange={() => handleFieldChange(relField.dotted_name)}
                                        />
                                        <span className="field-icon-edit small">{getFieldIcon(relField.type)}</span>
                                        <span className="field-label-edit">{relField.label}</span>
                                        <code className="field-name-edit">{relField.name}</code>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="fields-scroll-container">
                        <div className="fields-list">
                          {(editFlow.extraction_config?.fields || []).map(field => (
                            <span key={field} className="field-tag">
                              {field}
                              <button onClick={() => handleFieldChange(field)} className="remove-field">×</button>
                            </span>
                          ))}
                          <input
                            type="text"
                            className="add-field-input"
                            placeholder="Add field..."
                            onKeyDown={addCustomField}
                          />
                        </div>
                      </div>
                    )}
                    <div className="selected-fields-count">
                      {(editFlow.extraction_config?.fields || []).length} fields selected
                    </div>
                  </div>
                ) : (
                  <div className="fields-table-container">
                    {(flow.extraction_config?.fields || []).length > 0 ? (
                      <>
                        <div className="fields-search-container">
                          <input
                            type="text"
                            className="fields-search-input"
                            placeholder="Search by technical name or type..."
                            value={fieldSearchQuery}
                            onChange={(e) => setFieldSearchQuery(e.target.value)}
                          />
                        </div>
                        <table className="fields-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Technical Name</th>
                              <th>Label</th>
                              <th className="type-column-header">Type</th>
                              <th>Multi-level Object</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(flow.extraction_config?.fields || [])
                              .map((field, index) => {
                                const fieldName = typeof field === 'string' ? field : (field.name || field);
                                const fieldLabel = typeof field === 'object' ? (field.label || fieldName) : fieldName;
                                const fieldType = typeof field === 'object' ? (field.type || '') : '';

                                // Check if it's a multi-level field (contains dots)
                                const isMultiLevel = fieldName.includes('.');
                                let baseField = fieldName;
                                let multiLevelInfo = null;

                                if (isMultiLevel) {
                                  const parts = fieldName.split('.');
                                  baseField = parts[0];
                                  const relatedField = parts.slice(1).join('.');
                                  multiLevelInfo = {
                                    base: baseField,
                                    related: relatedField,
                                    full: fieldName
                                  };
                                }

                                return {
                                  fieldName,
                                  fieldLabel,
                                  fieldType,
                                  multiLevelInfo,
                                  originalIndex: index
                                };
                              })
                              .filter(({ fieldName, fieldType }) => {
                                if (!fieldSearchQuery.trim()) return true;
                                const query = fieldSearchQuery.toLowerCase();
                                return (
                                  fieldName.toLowerCase().includes(query) ||
                                  fieldType.toLowerCase().includes(query)
                                );
                              })
                              .map(({ fieldName, fieldLabel, fieldType, multiLevelInfo }, filteredIndex) => (
                                <tr key={fieldName || filteredIndex}>
                                  <td className="field-order">{filteredIndex + 1}</td>
                                  <td className="field-technical-name">
                                    <code>{fieldName}</code>
                                  </td>
                                  <td className="field-label">{fieldLabel}</td>
                                  <td className="field-type-cell">
                                    {fieldType ? (
                                      <div className="type-badge-wrapper">
                                        <span className="type-badge-new">
                                          <span className="type-icon">{getFieldIcon(fieldType)}</span>
                                          <span className="type-text">{fieldType.toUpperCase()}</span>
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="no-type">—</span>
                                    )}
                                  </td>
                                  <td className="field-multilevel">
                                    {multiLevelInfo ? (
                                      <div className="multilevel-info">
                                        <span className="multilevel-base">{multiLevelInfo.base}</span>
                                        <span className="multilevel-separator">→</span>
                                        <span className="multilevel-related">{multiLevelInfo.related}</span>
                                      </div>
                                    ) : (
                                      <span className="no-multilevel">—</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </>
                    ) : (
                      <div className="no-fields-message">
                        <span className="no-fields">No fields configured. All fields will be extracted.</span>
                      </div>
                    )}
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <td className="config-key">Created By</td>
              <td className="config-val">{flow.created_by_username || 'System'}</td>
              <td className="config-key">Last Run</td>
              <td className="config-val">{formatDate(flow.last_run_at)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Dependencies Section */}
      {flow && (
        <div className="dependencies-section">
          <div className="dependencies-header">
            <h2>Dependencies</h2>
            <button
              onClick={createDependencyFlows}
              className="btn-secondary"
              disabled={creatingDependencies}
            >
              {creatingDependencies ? 'Creating...' : 'Generate Dependency Flows'}
            </button>
          </div>
          {loadingDependencies ? (
            <div className="loading-deps">Loading dependencies...</div>
          ) : dependencies ? (
            <div className="dependencies-content">
              {dependencies.depends_on && dependencies.depends_on.length > 0 ? (
                <div className="depends-on">
                  <h3>This flow depends on:</h3>
                  <ul className="dependency-list">
                    {dependencies.depends_on.map((dep) => (
                      <li key={dep.id} className="dependency-item">
                        <span className="dep-icon">🔗</span>
                        <div className="dep-info">
                          {dep.type === 'self_referential' ? (
                            <>
                              <strong className="dep-link" style={{ cursor: 'default' }}>
                                {dep.name} (Self-referential)
                              </strong>
                              <span className="dep-model">Uses self-joins in BigQuery</span>
                              {dep.parent_field && (
                                <span className="dep-field">via {dep.parent_field}</span>
                              )}
                            </>
                          ) : (
                            <>
                              <strong onClick={() => navigate(`/flows/${dep.id}`)} className="dep-link">
                                {dep.name}
                              </strong>
                              <span className="dep-model">{dep.model}</span>
                              {dep.parent_field && (
                                <span className="dep-field">via {dep.parent_field}</span>
                              )}
                            </>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="no-dependencies">
                  <p>No dependencies found.</p>
                  <p className="help-text">
                    Enable "Generate Dependency Flows" to automatically create flows for related models (e.g., countries, states).
                  </p>
                </div>
              )}
              {dependencies.dependents && dependencies.dependents.length > 0 && (
                <div className="dependents">
                  <h3>Flows that depend on this:</h3>
                  <ul className="dependency-list">
                    {dependencies.dependents.map((dep) => (
                      <li key={dep.id} className="dependency-item">
                        <span className="dep-icon">⬅️</span>
                        <div className="dep-info">
                          <strong onClick={() => navigate(`/flows/${dep.id}`)} className="dep-link">
                            {dep.name}
                          </strong>
                          <span className="dep-model">{dep.model}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="no-dependencies">
              <p>Click "Generate Dependency Flows" to analyze multi-level fields and create related model flows.</p>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="section-tabs">
        <button
          className={`section-tab ${activeTab === 'runs' ? 'active' : ''}`}
          onClick={() => setActiveTab('runs')}
        >
          Run History
        </button>
        <button
          className={`section-tab ${activeTab === 'preview' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('preview');
            if (!previewData) fetchPreview('output', 10);
          }}
        >
          Data Preview
        </button>
      </div>

      {/* Tab Content */}
      <div className="section-content">
        {activeTab === 'runs' && (
          <div className="runs-section">
            {(!runs || runs.length === 0) && (!stats || !stats.recent_runs || stats.recent_runs.length === 0) ? (
              <div className="empty-runs">
                <p>No runs yet. Click "Run Pipeline" to execute this pipeline.</p>
              </div>
            ) : (
              <>
                {(runs && runs.length > 0) ? (
                  <table className="runs-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Run ID</th>
                        <th>Started</th>
                        <th>Duration</th>
                        <th>Records</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {runs.map((run) => {
                        const statusConfig = getStatusBadge(run.status);
                        return (
                          <tr key={run.id}>
                            <td>
                              <span className={`run-status status-${statusConfig.class}`}>
                                {statusConfig.icon} {run.status}
                              </span>
                            </td>
                            <td><code>{run.run_id}</code></td>
                            <td>{formatDate(run.started_at)}</td>
                            <td>{formatDuration(run.duration_seconds)}</td>
                            <td>
                              {run.records_extracted
                                ? `${run.records_loaded || 0} / ${run.records_extracted}`
                                : '—'}
                            </td>
                            <td>
                              <button
                                onClick={() => navigate(`/runs/${run.id}`)}
                                className="btn-link"
                              >
                                View →
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : stats?.recent_runs && stats.recent_runs.length > 0 ? (
                  <table className="runs-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Run ID</th>
                        <th>Started</th>
                        <th>Duration</th>
                        <th>Records</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recent_runs.map((run) => {
                        const statusConfig = getStatusBadge(run.status);
                        return (
                          <tr key={run.id}>
                            <td>
                              <span className={`run-status status-${statusConfig.class}`}>
                                {statusConfig.icon} {run.status}
                              </span>
                            </td>
                            <td><code>{run.run_id}</code></td>
                            <td>{formatDate(run.started_at)}</td>
                            <td>{formatDuration(run.duration_seconds)}</td>
                            <td>
                              {run.records_extracted
                                ? `${run.records_loaded || 0} / ${run.records_extracted}`
                                : '—'}
                            </td>
                            <td>
                              <button
                                onClick={() => navigate(`/runs/${run.id}`)}
                                className="btn-link"
                              >
                                View →
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty-runs">
                    <p>No runs yet. Click "Run Pipeline" to execute this pipeline.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'preview' && (
          <div className="preview-section">
            <div className="preview-controls">
              <div className="preview-type-btns">
                <button
                  className={`type-btn ${previewType === 'output' ? 'active' : ''}`}
                  onClick={() => fetchPreview('output', 10)}
                >
                  Output Data
                </button>
                <button
                  className={`type-btn ${previewType === 'error' ? 'active' : ''}`}
                  onClick={() => fetchPreview('error', 10)}
                >
                  Errors
                </button>
              </div>
              <button onClick={() => fetchPreview(previewType, 10)} className="btn-refresh">
                🔄 Refresh
              </button>
            </div>

            {previewLoading ? (
              <div className="preview-loading">Loading preview...</div>
            ) : previewData?.error ? (
              <div className="preview-error">
                <p>{previewData.error}</p>
                <p className="help-text">Run the pipeline first to see data here.</p>
              </div>
            ) : previewData ? (
              <div className="preview-data">
                <div className="preview-meta">
                  <span><strong>Table:</strong> {previewData.table}</span>
                  <span><strong>Rows:</strong> {previewData.total_rows?.toLocaleString()}</span>
                  <span><strong>Size:</strong> {previewData.size_mb} MB</span>
                </div>
                <div className="preview-table-wrap">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        {previewData.columns?.map((col) => (
                          <th key={col}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.rows?.map((row, idx) => (
                        <tr key={idx}>
                          {previewData.columns?.map((col) => (
                            <td key={col}>
                              {row[col] === null ? (
                                <span className="null-val">NULL</span>
                              ) : typeof row[col] === 'boolean' ? (
                                row[col] ? '✓' : '✗'
                              ) : (
                                String(row[col])
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="preview-empty">
                Select a table type above to preview data.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlowDetail;
